/**
 * Package Action - WHAT Section
 *
 * Edit mode UI for assigning a package.
 * Used inline within AutomationCard when editing the "What" section.
 *
 * V2 Reference: v2/app/templates/components/client-reminder-packages.hbs
 *
 * Fields:
 * - reminderOnId: Selected package ID
 * - startTime: Days after to activate (number)
 * - endTime: Days after activation to expire (number, optional)
 *   - V2 uses endTime = -1 to indicate "charge for this package"
 *   - When charging, endTime is -1; when not charging, endTime is null or a positive number
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Checkbox } from 'src/components/Checkbox';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { fetchPackageOptions } from 'src/models/package/package';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';

import { ActionWhatSectionProps } from '../automationActions';

/**
 * Package WHAT Section - Edit mode
 */
export const PackageWhatSection: FC<ActionWhatSectionProps> = ({
  automation,
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  // Fetch available packages
  const { data: packages = [], isLoading } = useQuery({
    queryKey: ['packages'],
    queryFn: () => fetchPackageOptions(),
    staleTime: 60000,
  });

  const packageOptions = packages.map((p) => ({
    value: p.id.toString(),
    label: p.name,
  }));

  // V2 uses endTime = -1 to indicate "charge for this package"
  const chargeForPackage = automation.endTime === -1;

  return (
    <Stack spacing={2}>
      {/* Package selector */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box>{t('automations.package.label')}</Box>
        <Box sx={{ minWidth: 200 }}>
          <FormSelect
            name={`automations.${index}.reminderOnId`}
            options={packageOptions}
            prompt={isLoading ? t('automations.common.loading') : t('automations.package.selectPackage')}
            onChange={(e) => {
              setFieldValue(`automations.${index}.reminderOnId`, e.target.value);
              // V2 sets reminderOnType to 'Fbm::Package'
              setFieldValue(`automations.${index}.reminderOnType`, 'Fbm::Package');
              // V2 clears endTime when package changes
              setFieldValue(`automations.${index}.endTime`, null);
            }}
          />
        </Box>
      </Box>

      {/* Activation timing */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box>{t('automations.package.activates')}</Box>
        <TextField
          type="number"
          value={automation.startTime || 0}
          onChange={(e) =>
            setFieldValue(
              `automations.${index}.startTime`,
              parseInt(e.target.value) || 0
            )
          }
          sx={{ width: 80 }}
          size="small"
          InputProps={{ inputProps: { min: 0 } }}
        />
        <Box>{chargeForPackage ? t('automations.package.daysAfter') + '.' : t('automations.package.daysAfterAndExpires')}</Box>
      </Box>

      {/* Expiration timing - only when not charging */}
      {!chargeForPackage && (
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <TextField
            type="number"
            value={automation.endTime || ''}
            onChange={(e) =>
              setFieldValue(
                `automations.${index}.endTime`,
                e.target.value ? parseInt(e.target.value) : null
              )
            }
            placeholder={t('automations.package.never')}
            sx={{ width: 80 }}
            size="small"
            InputProps={{ inputProps: { min: 0 } }}
          />
          <Box>{t('automations.package.daysAfterActivation')}</Box>
        </Box>
      )}

      {/* Charge option - V2 uses endTime = -1 to indicate charging */}
      <Box>
        <Checkbox
          label={t('automations.package.chargeForPackage')}
          checked={chargeForPackage}
          onChange={(e) => {
            // V2 pattern: endTime = -1 means charge, null means no charge
            setFieldValue(
              `automations.${index}.endTime`,
              e.target.checked ? -1 : null
            );
          }}
        />
      </Box>
    </Stack>
  );
};

/**
 * Package What Summary Component
 */
export const PackageWhatSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();
  const { data: packages = [] } = useQuery({
    queryKey: ['packages'],
    queryFn: () => fetchPackageOptions(),
    staleTime: 60000,
  });

  if (!automation.reminderOnId) {
    return <i>{t('automations.package.noPackageSelected')}</i>;
  }

  const pkg = packages.find(
    (p) => p.id.toString() === automation.reminderOnId?.toString()
  );
  const pkgName = pkg?.name || `Package #${automation.reminderOnId}`;
  // V2 uses endTime = -1 to indicate "charge for this package"
  const chargeForPackage = automation.endTime === -1;

  return (
    <>
      <b>{pkgName}</b> {t('automations.package.activates')} {automation.startTime || 0} {t('automations.package.daysAfter')}
      {chargeForPackage ? (
        <>
          . <b>{t('automations.package.noteWillBeCharged')}</b>
        </>
      ) : (
        <>
          {automation.endTime
            ? ` ${t('automations.package.andEnds', { days: automation.endTime })}`
            : ''}
          <b> {t('automations.package.noteNoCharge')}</b>
        </>
      )}
    </>
  );
};
